/**
 * A small Markdown reader for descriptions.
 *
 * Descriptions are where the useful, unstructured knowledge about a part ends
 * up — pinout, quirks, which supplier's version is the good one — and that is
 * naturally a list with a bit of emphasis, not a paragraph of plain text.
 *
 * Written here rather than pulled in: a full Markdown library brings HTML
 * passthrough with it, which then has to be sanitised, and the deployment is
 * offline so every dependency is one more thing to carry. The result is parsed
 * into a description of the document, and the component renders it as React
 * elements — no HTML string is ever built, so there is nothing for a `<script>`
 * in a description to hook onto.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; content: Inline[] }
  | { kind: 'paragraph'; content: Inline[] }
  | { kind: 'bullets'; items: Inline[][] }
  | { kind: 'numbers'; items: Inline[][] }
  | { kind: 'quote'; content: Inline[] }
  | { kind: 'pre'; text: string }
  | { kind: 'rule' };

/** Only schemes a browser should follow from a description. `javascript:` is
 *  the reason this check exists at all. */
const SAFE_LINK = /^(https?:\/\/|mailto:|obsidian:|\/)/i;

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\\?\([^)\s]+\))/;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  for (const piece of text.split(INLINE)) {
    if (!piece) continue;
    if (piece.length > 4 && piece.startsWith('**') && piece.endsWith('**')) {
      out.push({ kind: 'strong', text: piece.slice(2, -2) });
    } else if (piece.length > 4 && piece.startsWith('__') && piece.endsWith('__')) {
      out.push({ kind: 'strong', text: piece.slice(2, -2) });
    } else if (piece.length > 2 && piece.startsWith('*') && piece.endsWith('*')) {
      out.push({ kind: 'em', text: piece.slice(1, -1) });
    } else if (piece.length > 2 && piece.startsWith('_') && piece.endsWith('_')) {
      out.push({ kind: 'em', text: piece.slice(1, -1) });
    } else if (piece.length > 2 && piece.startsWith('`') && piece.endsWith('`')) {
      out.push({ kind: 'code', text: piece.slice(1, -1) });
    } else if (piece.startsWith('[')) {
      const close = piece.indexOf(']');
      const label = piece.slice(1, close);
      const href = piece.slice(close + (piece[close + 1] === '\\' ? 3 : 2), -1);
      // An unusable link is shown as the text it was written as, rather than
      // silently dropped: the address may still be worth reading.
      if (SAFE_LINK.test(href)) out.push({ kind: 'link', text: label, href });
      else out.push({ kind: 'text', text: piece });
    } else {
      out.push({ kind: 'text', text: piece });
    }
  }
  return out;
}

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,3})\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^\s*([-*_])\1{2,}\s*$/;

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: 'paragraph', content: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';

    if (line.trim() === '') {
      flush();
      continue;
    }

    if (line.startsWith('```')) {
      flush();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      blocks.push({ kind: 'pre', text: body.join('\n') });
      continue;
    }

    if (RULE.test(line)) {
      flush();
      blocks.push({ kind: 'rule' });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length as 1 | 2 | 3,
        content: parseInline(heading[2] ?? ''),
      });
      continue;
    }

    const quote = QUOTE.exec(line);
    if (quote) {
      flush();
      blocks.push({ kind: 'quote', content: parseInline(quote[1] ?? '') });
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = NUMBER.exec(line);
    if (bullet || numbered) {
      flush();
      const kind = bullet ? 'bullets' : 'numbers';
      const previous = blocks[blocks.length - 1];
      const item = parseInline((bullet ?? numbered)![1] ?? '');
      if (previous && previous.kind === kind) previous.items.push(item);
      else blocks.push({ kind, items: [item] } as Block);
      continue;
    }

    paragraph.push(line.trim());
  }

  flush();
  return blocks;
}

/** The first few lines as plain text, for the collapsed view. Formatting is
 *  dropped rather than half-rendered: a cut-off heading reads as a mistake. */
export function firstLines(source: string, count: number): string {
  const lines = source
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return lines.slice(0, count).join('\n');
}

export const lineCount = (source: string): number =>
  source
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '').length;
